import { useState } from "react";
import {
  Text,
  Group,
  Button,
  rem,
  Container,
  Title,
  Paper,
  List,
} from "@mantine/core";
import { IconFile } from "@tabler/icons-react";
import { useContext } from "react";

import { AuthContext } from "../../services/context";
import { getCurrentWorkspace } from "../../services/userConfig";
import { toBase64 } from "../../services/helper";
import CSVDropzoneButton from "../CSVDropzone";
import { useImportSpeakers } from "../../api/speakers";

function ImportSpeakers() {
  const { user } = useContext(AuthContext);
  const currentWorkspace = getCurrentWorkspace(user);
  const currentEventId = currentWorkspace.eventId;

  const [file, setFile] = useState(null);

  const { mutate, isPending, data, isSuccess, reset } = useImportSpeakers();

  const rowLabel = (row) =>
    [row?.firstname, row?.lastname].filter(Boolean).join(" ") || "Unnamed row";

  const failedRows = [
    ...(data?.errors?.validationErrors ?? []).map(({ row, errors }) => ({
      label: rowLabel(row),
      reason: errors
        .map((error) =>
          `${error.params?.missingProperty ?? error.instancePath.replace("/", "")} ${error.message}`.trim()
        )
        .join(", "),
    })),
    ...(data?.errors?.insertErrors ?? []).map(({ speaker, error }) => ({
      label: rowLabel(speaker),
      reason: error?.message ?? "Could not be imported",
    })),
  ];

  const importData = {
    total: data?.total,
    successful: data?.successfullyInserted,
  };

  const handleSubmit = async () => {
    const base64file = await toBase64(file);

    const data = {
      file: {
        name: file.name,
        type: file.type,
        data: base64file,
      },
      eventId: currentEventId,
    };

    mutate(data);
  };

  return (
    <Container>
      <Title>Import speakers</Title>
      <Paper withBorder shadow="xs" p="lg" mt="sm">
        <Container
          bg="var(--mantine-color-gray-light)"
          style={{ borderRadius: 24 }}
          p="sm"
          mb="lg"
        >
          <Text fw={700} size="lg">
            File instructions:
          </Text>
          <List size="sm">
            <List.Item>File must be in .csv or .xlsx format</List.Item>
            <List.Item>File size must be less than 10mb</List.Item>
            <List.Item>
              File must contain columns: first name, last name
            </List.Item>
            <List.Item>
              Optional columns:
              <List withPadding size="sm">
                <List.Item>occupation, biography</List.Item>
              </List>
            </List.Item>
          </List>
        </Container>

        {!file && <CSVDropzoneButton onDrop={setFile} />}

        {file && (
          <>
            <IconFile style={{ width: rem(50), height: rem(50) }} />
            <Text mt={8}>{file.name}</Text>
            <Group mt={16} justify="flex-start">
              <Button
                onClick={handleSubmit}
                style={{ marginTop: rem(20) }}
                color="blue"
                disabled={!file || isSuccess}
                loading={isPending}
              >
                Submit
              </Button>
              <Button
                onClick={() => {
                  setFile(null);
                  reset();
                }}
                style={{ marginTop: rem(20) }}
                color="red"
                disabled={isPending}
              >
                Clear file
              </Button>
            </Group>
          </>
        )}
        {data && isSuccess && file && (
          <>
            <Text mt={20} c="green">
              Successfully uploaded. Imported {importData.successful} speakers
              out of {importData.total}.
            </Text>
            {failedRows.length > 0 && (
              <>
                <Text mt={12} fw={600} c="red">
                  {failedRows.length} speakers could not be imported:
                </Text>
                <List size="sm" mt={4}>
                  {failedRows.map((row, index) => (
                    <List.Item key={index}>
                      {row.label} —{" "}
                      <Text span c="dimmed">
                        {row.reason}
                      </Text>
                    </List.Item>
                  ))}
                </List>
              </>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
}

export default ImportSpeakers;
